/* eslint-disable no-console */

import type Pusher from 'pusher-js'
import type { Ref } from 'vue'
import { createSharedComposable } from '@vueuse/core'
import { DateTime } from 'luxon'
import { ref, toRef, watch } from 'vue'
import type {
  CameraImageUpdatedEvent,
  EmptyAppointmentSlotsLoadedEvent,
  EmptyReturnRulesCheckedEvent,
  EmptyReturnRulesReadingEvent,
  EmptyReturnRulesUpdatedEvent,
  EventName,
  ExportEmptyPickupSlotsLoadedEvent,
  GateScheduleUpdatedEvent,
  GlobalNotificationEvent,
  ImportSlotsUpdatedEvent,
  OrganizationNotificationEvent,
  UserNotificationEvent,
  VesselETAsUpdatedEvent,
  WebsocketChannel,
} from '~/services/apiClient'
import {
  getLastConnectedAndDisconnected,
  setLastConnected,
  setLastDisconnected,
} from '~/repositories/websocketConnectionState'
import { decodeSoketiEvent } from '~/logic/soketiEventDecoding'
import { connectToSoketi } from '~/services/soketi'
import { useUserStore } from '~/stores/user'

export type SoketiEvent =
  | OrganizationNotificationEvent
  | UserNotificationEvent
  | GlobalNotificationEvent
  | EmptyAppointmentSlotsLoadedEvent
  | CameraImageUpdatedEvent
  | GateScheduleUpdatedEvent
  | EmptyReturnRulesCheckedEvent
  | VesselETAsUpdatedEvent
  | EmptyReturnRulesReadingEvent
  | EmptyReturnRulesUpdatedEvent
  | ImportSlotsUpdatedEvent
  | ExportEmptyPickupSlotsLoadedEvent

export type SoketiEventHandler = (event: SoketiEvent) => void
type RawSoketiEventHandler = (event: string) => void
type SoketiConnectionStatus =
  | 'initialized'
  | 'connecting'
  | 'connected'
  | 'disconnected'
  | 'failed'
  | 'unavailable'

// Constants moved to websocketConnectionState.ts

function isPublicChannel(channelName: string): boolean {
  return (
    !channelName.startsWith('private-') && !channelName.startsWith('presence-')
  )
}

function useSoketiInner(readyToConnectToWebSocket: Ref<boolean>) {
  const connected = ref(false)
  const previouslyWasConnected = ref(false)
  const onReconnectCallbacks = ref(new Map<string, () => void>())
  const client: Pusher = connectToSoketi()
  const eventHandlers = ref(
    new Map<string, Map<string, RawSoketiEventHandler>>()
  )
  // Public subscriptions
  watch([connected], (isConnected) => {
    if (isConnected) {
      // Create subscriptions and event handlers
      for (const [channelName, channelEventHandlers] of eventHandlers.value) {
        if (!isPublicChannel(channelName)) {
          continue
        }
        // console.log(`Subscribing to ${channelName}`)
        const channel = client.subscribe(channelName)
        for (const [eventName, handler] of channelEventHandlers) {
          channel.bind(eventName, handler)
        }
      }
    }
  })
  // Private subscriptions
  watch(
    () => [connected.value, readyToConnectToWebSocket.value],
    ([isConnected, readyToConnectToWebSocket]) => {
      if (!readyToConnectToWebSocket) return
      if (isConnected) {
        console.log(`[useSoketiInner] Creating private subscriptions`)
        // Create subscriptions and event handlers
        for (const [channelName, channelEventHandlers] of eventHandlers.value) {
          if (isPublicChannel(channelName)) {
            continue
          }
          console.log(`Subscribing to ${channelName}`)
          const channel = client.subscribe(channelName)
          for (const [eventName, handler] of channelEventHandlers) {
            // TODO: make sure there is not already a bound event
            if (isBound(channelName, eventName as EventName)) {
              console.log(
                `Already have a handler for channel ${channelName} and event ${eventName}`
              )
            }
            channel.bind(eventName, handler)
          }
        }
      }
    }
  )

  function bindOnReconnect(callback: () => void) {
    // Register a function to run when we've lost connection and then reconnected
    if (onReconnectCallbacks.value.has(callback.name)) {
      throw new Error(
        `Already have a callback with name ${callback.name} for onReconnect`
      )
    }
    onReconnectCallbacks.value.set(callback.name, callback)
  }
  function onSoketiConnectionStateChange({
    previous,
    current,
  }: {
    previous: SoketiConnectionStatus
    current: SoketiConnectionStatus
  }) {
    console.debug(
      `[useSoketiInner] Connection state changed from ${previous} to ${current}`
    )
    if (current === 'connected') {
      console.log(`Soketi connected. Previous state: ${previous}`)
      connected.value = true
      setLastConnected(DateTime.now())
      if (previouslyWasConnected.value) {
        // Reconnect case
        console.log('Running onReconnectCallbacks')
        for (const callback of onReconnectCallbacks.value.values()) {
          callback()
        }
      }
      previouslyWasConnected.value = true
    } else {
      console.log(`Soketi disconnected. Previous state: ${previous}`)
      connected.value = false
      setLastDisconnected(DateTime.now())
    }
  }
  // Handle connection/disconnect
  // we shouldn't try to reconnect to org specific channels if we have a new auth header

  client.connection.bind('state_change', onSoketiConnectionStateChange)
  watch(
    readyToConnectToWebSocket,
    (isLoggedIn) => {
      if (isLoggedIn) {
        client.connection.bind('state_change', onSoketiConnectionStateChange)
      }
    },
    { immediate: true }
  )
  function bind(
    channelName: WebsocketChannel | string,
    eventName: EventName,
    handler: SoketiEventHandler
  ) {
    const wrappedEventHandler = handlerWrapper(handler)
    let channelEventHandlers = eventHandlers.value.get(channelName)
    if (!channelEventHandlers) {
      channelEventHandlers = new Map<string, RawSoketiEventHandler>()
      eventHandlers.value.set(channelName, channelEventHandlers)
    }
    if (channelEventHandlers.has(eventName)) {
      // We could allow multiple event handlers for one event, but for now let's not
      throw new Error(
        `Already have a handler for channel ${channelName} and event ${eventName}`
      )
    }
    channelEventHandlers.set(eventName, wrappedEventHandler)
    if (connected.value) {
      if (!isPublicChannel(channelName) && !readyToConnectToWebSocket.value)
        return
      // From the Pusher code, it looks totally fine to call .subscribe many times. If
      // we are already subscribed, it's basically a "get subscription" call.
      console.debug(
        `[useSoketiInner] Binding event ${eventName} to channel ${channelName}. Connected: ${
          connected.value
        }, Logged in: ${readyToConnectToWebSocket.value}`
      )
      const channel = client.subscribe(channelName)
      channel.bind(eventName, wrappedEventHandler)
    }
  }
  function unbind(
    channelName: WebsocketChannel | string,
    eventName: EventName,
    _handler: SoketiEventHandler
  ) {
    const channel = client.subscribe(channelName)
    const channelEventHandlers = eventHandlers.value.get(channelName)
    if (channelEventHandlers) {
      const wrappedHandler = channelEventHandlers.get(eventName)
      if (wrappedHandler) {
        channel.unbind(eventName, wrappedHandler)
        channelEventHandlers.delete(eventName)
      }
    }
    // Remove and unsubscribe from channel if there are no more event handlers
    if (!channelEventHandlers || channelEventHandlers.size === 0) {
      eventHandlers.value.delete(channelName)
      console.log(`Unsubscribing from ${channelName}`)
      client.unsubscribe(channelName)
    }
  }
  function isBound(
    channelName: WebsocketChannel | string,
    eventName: EventName
  ) {
    const channelEventHandlers = eventHandlers.value.get(channelName)
    if (!channelEventHandlers) {
      return false
    }
    const bound = channelEventHandlers.has(eventName)
    // Double check that the client is subscribed to the channel
    if (bound) {
      const channel = client.subscribe(channelName)
      if (!channel.subscribed) {
        console.warn(
          `Event handler for channel ${channelName} and event ${eventName} is bound, but client is not subscribed to channel`
        )
      }
    }
    return bound
  }
  return {
    connected,
    bind,
    unbind,
    isBound,
    eventHandlers,
    bindOnReconnect,
    getLastConnectedAndDisconnected,
  }
}
// Make sure there is only ever one Soketi connection
export const useSoketiBase = createSharedComposable(useSoketiInner)

export function useSoketi() {
  const userStore = useUserStore()
  const readyToConnectToWebSocket = toRef(
    userStore,
    'readyToConnectToWebSocket'
  )
  return useSoketiBase(readyToConnectToWebSocket)
}

function handlerWrapper(handler: SoketiEventHandler) {
  return function (b64EncodedCompressedEvent: string) {
    const event = decodeSoketiEvent(b64EncodedCompressedEvent) as SoketiEvent
    handler(event)
  }
}
